import PropTypes from "prop-types";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { PlusIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import { FieldError } from "@/components/ui/field";
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { FormTextarea, SectionCard } from "components/BaseForm";
import { buildPrice, getInvoicesTotalValue } from "components/helpers";
import { emptyInvoiceLine } from "routes/revenue_opportunity/form/invoiceLines";
import InvoiceRow from "routes/revenue_opportunity/form/sections/invoices/InvoiceRow";

// The table uses a fixed layout, so these header widths set every column and
// a validation message cannot widen one. Product takes the remaining width.
const COLUMNS = [
  { label: "Product", required: true },
  { label: "Amount", required: true, className: "tw:w-32" },
  { label: "Term", required: true, className: "tw:w-24" },
  { label: "Qty", required: true, className: "tw:w-24" },
  { label: "License Start", className: "tw:w-40" },
  { label: "License End", className: "tw:w-40" },
  { label: "Total", className: "tw:w-32 tw:text-right" },
];

const InvoicesSection = ({ productOptions, disabledProductOptions }) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const { fields, append, remove } = useFieldArray({
    control,
    name: "invoices",
  });
  const invoices = useWatch({ name: "invoices" }) ?? [];
  const overrideIndex = invoices.findIndex(
    line => line.is_total_value_override
  );
  // zod reports the empty-list rule on the array itself.
  const listError = errors.invoices?.message ?? errors.invoices?.root?.message;

  return (
    <SectionCard title="Invoices">
      {fields.length > 0 && (
        <Table className="tw:table-fixed">
          <TableHeader>
            <TableRow>
              {COLUMNS.map(column => (
                <TableHead key={column.label} className={column.className}>
                  {column.label}
                  {column.required && (
                    <span className="tw:text-destructive"> *</span>
                  )}
                </TableHead>
              ))}
              <TableHead className="tw:w-12" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {fields.map((field, index) => (
              <InvoiceRow
                key={field.id}
                index={index}
                productOptions={productOptions}
                disabledProductOptions={disabledProductOptions}
                hasOtherOverride={
                  overrideIndex !== -1 && overrideIndex !== index
                }
                onRemove={remove}
              />
            ))}
          </TableBody>
        </Table>
      )}
      <FieldError>{listError}</FieldError>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="tw:self-start"
        onClick={() => append(emptyInvoiceLine())}
      >
        <PlusIcon data-icon="inline-start" /> Add Invoice Line
      </Button>
      <div className="tw:grid tw:grid-cols-1 tw:gap-5 tw:md:grid-cols-[1fr_auto]">
        <FormTextarea name="comments" label="Comments" rows={3} />
        <div className="tw:flex tw:flex-col tw:items-end tw:justify-center tw:gap-1 tw:px-4">
          <span className="tw:text-sm tw:text-muted-foreground">
            Total Value
          </span>
          <span className="tw:text-2xl tw:font-semibold tw:text-foreground">
            {buildPrice(getInvoicesTotalValue(invoices))}
          </span>
        </div>
      </div>
    </SectionCard>
  );
};

const optionShape = PropTypes.shape({
  key: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  text: PropTypes.string,
});

InvoicesSection.propTypes = {
  productOptions: PropTypes.arrayOf(optionShape).isRequired,
  disabledProductOptions: PropTypes.arrayOf(optionShape).isRequired,
};

export default InvoicesSection;
